/* ==========================================================================
   ARCTA · Add-to-Cart Popup-Flow — LIVE
   Popup 1: Add-ons (Größen-Matching zur gewählten Zelt-Variante) + /cart/add.js
   Popup 2: Erfolg + Ausrüstungs-Karussell (+ /cart/add.js pro Produkt)
   ========================================================================== */

(() => {
  if (customElements.get('arcta-atc-popups')) return;

  const FOCUSABLE =
    'button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

  /* Größen-Token aus einem Titel/Optionswert ziehen:
     "Family" -> 190, sonst die erste Zahl ("140", "140cm", "Viator (PRO) 140"). */
  function sizeToken(s) {
    if (!s) return '';
    const t = String(s).toLowerCase();
    if (t.indexOf('family') !== -1) return '190';
    const n = t.match(/\d+/);
    return n ? n[0] : '';
  }

  /* Größen-Token aus einem PRODUKT-Titel ziehen.
     Anders als sizeToken() wird hier die LETZTE Zahl genommen, weil in
     Produktnamen oft weitere Zahlen stehen ("Viator 2.0 160" -> 160).
     Wird nur als Fallback benutzt, wenn der Varianten-Titel nichts hergibt —
     z.B. bei Produkten wie "Viator 160" / "Viator Family", die keine
     Größenvariante haben (Varianten-Titel = "Default Title"). */
  function titleToken(s) {
    if (!s) return '';
    const t = String(s).toLowerCase();
    if (t.indexOf('family') !== -1) return '190';
    const all = t.match(/\d+/g);
    return all ? all[all.length - 1] : '';
  }

  /* Seiten-Token (links/rechts) aus einem Titel oder Optionswert ziehen.
     Nötig für seitenspezifisches Zubehör, z.B. der Vorraum der Wing 270:
     "200cm rechts" -> 'r'. Ohne das würde nur die Breite gematcht und
     innerhalb der Breite die erste verfügbare Variante geraten.
     Erkennt: links/linke/linker/left/l und rechts/rechte/rechter/right/r */
  function sideToken(s) {
    if (!s) return '';
    const t = ' ' + String(s).toLowerCase().replace(/[^a-zäöüß0-9]+/g, ' ') + ' ';
    if (/ (rechts?|rechte[rs]?|right|r) /.test(t)) return 'r';
    if (/ (links?|linke[rs]?|left|l) /.test(t)) return 'l';
    return '';
  }

  /* Seite einer Add-on-Variante — kann in Option 1, Option 2 oder im
     Varianten-Titel stehen, deshalb alle drei zusammen prüfen. */
  function variantSide(v) {
    return sideToken([v.option1, v.option2, v.title].join(' '));
  }

  function formatEuro(cents) {
    return (cents / 100).toLocaleString('de-DE', {
      minimumFractionDigits: 2, maximumFractionDigits: 2
    }) + ' €';
  }

  class ArctaAtcPopups extends HTMLElement {
    connectedCallback() {
      this._lastFocused = null;
      this._addTimers = new Map();
      this._parentId = this.getAttribute('data-parent-product-id') || '';
      // Zelt-Varianten (ID -> Titel/Preis) fürs Größen-Mapping + Gesamtsumme
      try { this._parentVariants = JSON.parse(this.getAttribute('data-parent-variants')) || []; }
      catch (e) { this._parentVariants = []; }
      // aktuelle Zelt-Variante (für Preis in der Gesamtsumme)
      const cvid = this.getAttribute('data-current-variant-id');
      this._parentVariant = this._parentVariants.find((x) => String(x.id) === String(cvid)) || this._parentVariants[0] || null;
      this._cartTotal = null; // echte Warenkorbsumme (wird bei open() gelesen)
      // Produkt-Titel als Fallback-Quelle für die Größe (Produkte ohne Größenvariante)
      this._productTitle = this.getAttribute('data-product-title') || '';
      // aktuelle Zelt-Größe (Token) — initial aus dem Attribut, updated per Event/Add
      this._setTokens(this.getAttribute('data-current-variant-title'));

      this.addEventListener('click', (e) => this._onClick(e));
      this.addEventListener('keydown', (e) => this._onKeydown(e));
      this.addEventListener('change', (e) => this._onChange(e));

      // Auf Varianten-Wechsel in der Buybox hören (setzt die Zelt-Größe)
      document.addEventListener('arcta:variant-changed', (e) => {
        const d = e && e.detail;
        if (d && d.productId && this._parentId && String(d.productId) !== String(this._parentId)) return;
        if (d && d.variantTitle) {
          this._setTokens(d.variantTitle);
          this._applyMatching();
        }
      });

      this._applyMatching();
      this._updateSum();
    }

    disconnectedCallback() {
      this._addTimers.forEach((t) => clearTimeout(t));
      this._addTimers.clear();
      document.documentElement.classList.remove('arcta-atc-lock');
      document.body.classList.remove('arcta-atc-open');
    }

    /* Größen- UND Seiten-Token aus dem aktuellen Varianten-Titel ermitteln.
       Größe zweistufig:
       1. Varianten-Titel ("160", "Family", "200cm links") — Produkte MIT Variante
       2. Produkt-Titel — Produkte OHNE Größenvariante, deren Varianten-Titel
          "Default Title" ist, z.B. "Viator 160", "Viator Family". */
    _setTokens(variantTitle) {
      this._sizeToken = sizeToken(variantTitle) || titleToken(this._productTitle);
      this._sideToken = sideToken(variantTitle) || sideToken(this._productTitle);
    }

    /* ---------- öffentliche API ---------- */
    // Größe direkt aus einer (Zelt-)Varianten-ID setzen — zuverlässiger als Events.
    setSizeFromVariantId(variantId) {
      if (!variantId) return;
      const v = (this._parentVariants || []).find((x) => String(x.id) === String(variantId));
      if (v) {
        this._setTokens(v.title);
        this._parentVariant = v; // für die Gesamtsumme (Zeltpreis)
        this._applyMatching();
        this._updateSum();
      }
    }

    open(step = 1, variantId) {
      if (variantId) this.setSizeFromVariantId(variantId);
      this._lastFocused = document.activeElement;
      this.hidden = false;
      this._applyMatching();   // Größe beim Öffnen frisch anwenden
      this._showStep(step);
      document.documentElement.classList.add('arcta-atc-lock');
      document.body.classList.add('arcta-atc-open');
      // Warenkorb abgleichen: was liegt schon drin? (Badges, Stepper, Summe)
      // Mehrfach, weil Apps (z.B. Gratis-Beigaben) verzögert nachlegen.
      this._refreshCartState();
      setTimeout(() => { if (!this.hidden) this._refreshCartState(); }, 1400);
      setTimeout(() => { if (!this.hidden) this._refreshCartState(); }, 3200);
    }

    close() {
      this.querySelectorAll('.arcta-atc__dialog').forEach((d) => (d.hidden = true));
      this.hidden = true;
      document.documentElement.classList.remove('arcta-atc-lock');
      document.body.classList.remove('arcta-atc-open');
      if (this._lastFocused && this._lastFocused.focus) this._lastFocused.focus();
    }

    // Theme-Cart-Drawer öffnen (Motion). Nutzt die echte Theme-Drawer-API:
    // theme.Drawers('CartDrawer','cart').open() — genau wie das Theme selbst.
    // Kleiner Delay, damit der Drawer-Wächter (main-product) nach dem
    // Popup-Close gestoppt hat, bevor wir öffnen.
    _openCartDrawer() {
      setTimeout(function () {
        // 1) Echte Theme-API (bevorzugt)
        try {
          if (window.theme && theme.Drawers) {
            var d = new theme.Drawers('CartDrawer', 'cart');
            if (d && typeof d.open === 'function') { d.open(); return; }
          }
        } catch (e) {}
        // 2) Fallback: den offenen Zustand direkt setzen
        var drawer = document.querySelector('#CartDrawer');
        if (drawer) {
          document.documentElement.classList.add('js-drawer-open', 'lock-scroll');
          drawer.classList.add('is-open', 'drawer--is-open');
          drawer.setAttribute('aria-hidden', 'false');
          var overlay = document.querySelector('.page-overlay, .js-overlay');
          if (overlay) overlay.classList.add('page-overlay--visible', 'is-visible', 'is-active');
          return;
        }
        // 3) allerletzter Fallback: Cart-Seite
        window.location.href = '/cart';
      }, 320);
    }

    /* ---------- Größen-Matching ---------- */
    _variantsOf(addonEl) {
      try { return JSON.parse(addonEl.getAttribute('data-variants')) || []; }
      catch (e) { return []; }
    }

    // Für ein Add-on: passende Variante(n) zur aktuellen Zelt-Größe finden.
    // WICHTIG: Kein Treffer = kein Angebot. Es wird NIE auf eine andere Größe
    // zurückgefallen (früher wurde sonst z.B. das 140er Vorzelt beim Viator 160
    // oder Viator Family angeboten).
    // Rückgabe: { match: bestVariant|null, sameWidth: [Varianten mit passender Breite] }
    _matchFor(addonEl) {
      const variants = this._variantsOf(addonEl);
      const want = this._sizeToken;

      // Add-on ohne Größenauswahl (nur eine Variante): da gibt es nichts zu
      // matchen, also immer anbieten.
      if (variants.length <= 1) {
        return { match: variants[0] || null, sameWidth: variants };
      }

      // Zelt-Größe nicht ermittelbar, Add-on hat aber mehrere Größen:
      // lieber nichts anbieten als die falsche Größe raten.
      if (!want) {
        return { match: null, sameWidth: [] };
      }

      let sameWidth = variants.filter((v) => sizeToken(v.option1) === want);

      // Seiten-Filter (links/rechts) — z.B. Vorraum der Wing 270.
      // Greift nur, wenn die Markise/das Zelt eine Seite vorgibt UND das Add-on
      // überhaupt seitenspezifische Varianten hat. Add-ons ohne Seite (Matratze,
      // Tarp) bleiben davon unberührt.
      if (want && this._sideToken && sameWidth.some((v) => variantSide(v))) {
        // Bewusst strikt: gibt es die passende Seite nicht, wird nichts
        // angeboten statt die falsche Seite zu verkaufen.
        sameWidth = sameWidth.filter((v) => variantSide(v) === this._sideToken);
      }

      // bevorzugt verfügbare — aber ausschließlich innerhalb der passenden Größe/Seite
      const avail = sameWidth.filter((v) => v.available);
      const match = (avail[0] || sameWidth[0]) || null;
      return { match: match, sameWidth: sameWidth };
    }

    // Badge/Preis/Dropdown pro Add-on setzen
    _applyMatching() {
      this.querySelectorAll('[data-arcta-atc-addon]').forEach((el) => {
        const hasTwo = el.getAttribute('data-has-two-options') === 'true';
        const res = this._matchFor(el);
        const badge = el.querySelector('[data-arcta-atc-sizebadge]');
        const priceEl = el.querySelector('[data-arcta-atc-price]');

        // Keine passende Größe für diese Zelt-Variante -> Add-on ausblenden,
        // abwählen und Varianten-ID entfernen (kann so nicht in den Warenkorb).
        const usable = hasTwo ? res.sameWidth : (res.match ? [res.match] : []);
        if (!usable.length) {
          el.hidden = true;
          el.setAttribute('aria-checked', 'false');
          el.removeAttribute('data-variant-id');
          return;
        }
        el.hidden = false;

        if (!hasTwo) {
          // Einfaches Add-on: gematchte Variante = fertige Auswahl
          const v = res.match;
          if (v) {
            el.dataset.variantId = v.id;
            if (badge) badge.textContent = v.option1 || '';
            if (priceEl) priceEl.textContent = '+ ' + formatEuro(v.price);
            this._setRate(el, v.price);
          }
        } else {
          // Zwei Optionen (z.B. Vorzelt): Breite gematcht, Höhe per Dropdown
          const select = el.querySelector('[data-arcta-atc-option2]');
          const pool = res.sameWidth; // Varianten mit passender Breite
          if (select) {
            select.innerHTML = '';
            pool.forEach((v) => {
              const opt = document.createElement('option');
              opt.value = v.id;
              opt.textContent = v.option2 || v.title;
              opt.dataset.price = v.price;
              if (!v.available) opt.disabled = true;
              select.appendChild(opt);
            });
            // erste (verfügbare) Höhe vorbelegen
            const firstAvail = pool.find((v) => v.available) || pool[0];
            if (firstAvail) {
              select.value = firstAvail.id;
              el.dataset.variantId = firstAvail.id;
              if (priceEl) priceEl.textContent = '+ ' + formatEuro(firstAvail.price);
              this._setRate(el, firstAvail.price);
            }
          }
          // Breite als Badge
          const w = pool[0] ? pool[0].option1 : '';
          if (badge) badge.textContent = w || '';
        }
      });

      // Nummerierung neu vergeben — Liquid zählt ALLE Add-ons, auch die
      // ausgeblendeten. Ohne das entstehen Lücken (1, 3, 4).
      let visNum = 0;
      this.querySelectorAll('[data-arcta-atc-addon]').forEach((el) => {
        if (el.hidden) return;
        visNum++;
        const numEl = el.querySelector('.arcta-atc__addon-num');
        if (numEl) numEl.textContent = visNum;
      });

      this._updateSum();
    }

    /* ---------- Events ---------- */
    _onChange(e) {
      const select = e.target.closest('[data-arcta-atc-option2]');
      if (!select) return;
      const el = select.closest('[data-arcta-atc-addon]');
      const opt = select.options[select.selectedIndex];
      if (el && opt) {
        el.dataset.variantId = opt.value;
        const priceEl = el.querySelector('[data-arcta-atc-price]');
        if (priceEl && opt.dataset.price) priceEl.textContent = '+ ' + formatEuro(Number(opt.dataset.price));
        this._setRate(el, Number(opt.dataset.price) || 0);
        this._updateSum();
      }
    }

    _onClick(e) {
      // "Zum Warenkorb": Popup schließen + Theme-Cart-Drawer öffnen (statt /cart)
      const openCartEl = e.target.closest('[data-arcta-atc-opencart]');
      if (openCartEl) {
        e.preventDefault();
        this.close();
        this._openCartDrawer();
        return;
      }
      // X oben rechts: Popup zu + Cart-Drawer öffnen (das Zelt liegt ja drin)
      const closeCartEl = e.target.closest('[data-arcta-atc-closecart]');
      if (closeCartEl) {
        e.preventDefault();
        this.close();
        this._openCartDrawer();
        return;
      }

      const closeEl = e.target.closest('[data-arcta-atc-close]');
      if (closeEl) {
        if (closeEl.tagName === 'A' && closeEl.getAttribute('href') === '#') e.preventDefault();
        if (closeEl.tagName !== 'A' || closeEl.getAttribute('href') === '#') this.close();
        return;
      }
      const nextBtn = e.target.closest('[data-arcta-atc-next]');
      if (nextBtn) { this._continue(nextBtn); return; }

      const minusBtn = e.target.closest('[data-arcta-atc-minus]');
      if (minusBtn) { e.stopPropagation(); this._minusGear(minusBtn); return; }

      const addBtn = e.target.closest('[data-arcta-atc-add]');
      if (addBtn) { e.stopPropagation(); this._addGear(addBtn); return; }

      // Klick auf die Auswahl-Zeile toggelt die Checkbox (nicht auf dem Dropdown!)
      const selrow = e.target.closest('.arcta-atc__addon-selectrow');
      if (selrow && !e.target.closest('[data-arcta-atc-option2]')) {
        const addon = selrow.closest('[data-arcta-atc-addon]');
        if (addon.classList.contains('is-incart')) return; // liegt schon im Warenkorb
        const on = addon.getAttribute('aria-checked') === 'true';
        addon.setAttribute('aria-checked', on ? 'false' : 'true');
        this._updateSum();
        return;
      }

      const arrow = e.target.closest('[data-arcta-atc-scroll]');
      if (arrow) {
        const carousel = this.querySelector('.arcta-atc__dialog:not([hidden]) .arcta-atc__carousel');
        if (carousel) {
          const dir = arrow.dataset.arctaAtcScroll === 'prev' ? -1 : 1;
          carousel.scrollBy({ left: dir * 200, behavior: 'smooth' });
        }
      }
    }

    _onKeydown(e) {
      if (e.key === 'Escape') { this.close(); return; }
      if (e.key === 'Tab') {
        const panel = this.querySelector('.arcta-atc__dialog:not([hidden]) .arcta-atc__panel');
        if (!panel) return;
        const items = Array.from(panel.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);
        if (!items.length) return;
        const first = items[0], last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }

    _selectedAddons() {
      return Array.from(this.querySelectorAll('[data-arcta-atc-addon][aria-checked="true"]'))
        .map((el) => ({ id: el.dataset.arctaAtcAddon, variantId: el.dataset.variantId || null }))
        .filter((a) => a.variantId);
    }

    /* ---------- Warenkorb ---------- */
    _continue(btn) {
      const addons = this._selectedAddons();
      if (!addons.length) {
        // nichts gewählt -> einfach zu Popup 2 (überspringen)
        this.dispatchEvent(new CustomEvent('arcta-atc:continue', { detail: { addons: [] }, bubbles: true }));
        this._showStep(2);
        this._refreshCartState();
        return;
      }
      // gewählte Varianten sammeln in EINEM /cart/add.js-Call
      const items = addons.map((a) => ({ id: Number(a.variantId), quantity: 1 }));
      if (btn) { btn.disabled = true; btn.textContent = 'Wird hinzugefügt…'; }
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then((r) => { if (!r.ok) throw new Error('cart'); return r.json(); })
        .then(() => {
          document.dispatchEvent(new CustomEvent('ajaxProduct:added'));
          document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
          this.dispatchEvent(new CustomEvent('arcta-atc:continue', { detail: { addons: addons }, bubbles: true }));
          if (btn) { btn.disabled = false; btn.textContent = 'Weiter'; }
          this._showStep(2);
          this._refreshCartState();
        })
        .catch(() => {
          if (btn) { btn.disabled = false; btn.textContent = 'Weiter'; }
          this._showStep(2); // trotzdem weiter, Zelt liegt ja im Korb
          this._refreshCartState();
        });
    }

    _addGear(btn) {
      if (btn.disabled) return;
      const wrap = btn.closest('[data-arcta-atc-qty]');
      const variantId = btn.dataset.variantId;
      if (!wrap || !variantId) return;
      btn.disabled = true;
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
      })
        .then((r) => { if (!r.ok) throw new Error('cart'); return r.json(); })
        .then(() => {
          document.dispatchEvent(new CustomEvent('ajaxProduct:added'));
          document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
          this._setGearCount(wrap, this._gearCount(wrap) + 1); // sofortiges Feedback
          this._refreshCartState();                            // Wahrheit vom Server
          btn.disabled = false;
        })
        .catch(() => { btn.disabled = false; });
    }

    // Minus: reduziert die Variante im Warenkorb um 1 (echte Cart-Menge wird
    // gelesen, damit auch vorher vorhandene Stücke korrekt behandelt werden)
    _minusGear(btn) {
      if (btn.disabled) return;
      const wrap = btn.closest('[data-arcta-atc-qty]');
      if (!wrap) return;
      const variantId = wrap.dataset.variantId;
      const localCount = this._gearCount(wrap);
      if (!variantId || localCount <= 0) return;
      btn.disabled = true;
      fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } })
        .then((r) => r.json())
        .then((cart) => {
          const item = (cart.items || []).find((it) => String(it.variant_id) === String(variantId));
          if (!item) return null;
          return fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: item.key, quantity: Math.max(0, item.quantity - 1) })
          });
        })
        .then((r) => (r ? r.json() : null))
        .then(() => {
          document.dispatchEvent(new CustomEvent('ajaxProduct:removed'));
          document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
          btn.disabled = false;                     // erst entsperren ...
          this._setGearCount(wrap, localCount - 1); // ... dann sperrt das hier bei 0 wieder
          this._refreshCartState();                 // Wahrheit vom Server
        })
        .catch(() => { btn.disabled = false; });
    }

    _gearCount(wrap) {
      return parseInt(wrap.querySelector('[data-arcta-atc-count]').textContent, 10) || 0;
    }

    // Zähler setzen: Stepper ist immer sichtbar ([− 0 +]),
    // Minus ist bei 0 gesperrt. Danach Gesamtsumme aktualisieren.
    _setGearCount(wrap, n) {
      n = Math.max(0, n);
      const countEl = wrap.querySelector('[data-arcta-atc-count]');
      const minusEl = wrap.querySelector('[data-arcta-atc-minus]');
      countEl.textContent = n;
      minusEl.disabled = n <= 0;
      wrap.classList.toggle('is-active', n > 0);
      this._updateTotal2();
    }

    _showStep(step) {
      this.querySelectorAll('.arcta-atc__dialog').forEach((d) => {
        d.hidden = Number(d.dataset.arctaAtcStep) !== step;
      });
      const panel = this.querySelector('.arcta-atc__dialog:not([hidden]) .arcta-atc__panel');
      if (panel) panel.focus({ preventScroll: true });
    }

    // Raten-Chip an einer Add-on-Kachel setzen ("Ab X €/Monat", abgerundet).
    // Monate kommen aus dem Theme-Editor (data-installments, 0 = aus).
    _setRate(el, cents) {
      const rateEl = el.querySelector('[data-arcta-atc-rate]');
      if (!rateEl) return;
      const months = parseInt(this.getAttribute('data-installments'), 10) || 0;
      const eur = months > 0 && cents ? Math.floor(cents / months / 100) : 0;
      if (eur >= 1) {
        rateEl.textContent = 'Ab ' + eur + ' \u20AC/Monat';
        rateEl.hidden = false;
      } else {
        rateEl.hidden = true;
      }
    }

    // Summe der aktuell angehakten Extras (in Cent)
    _extrasTotal() {
      let total = 0;
      this.querySelectorAll('[data-arcta-atc-addon][aria-checked="true"]').forEach((el) => {
        const variants = this._variantsOf(el);
        const v = variants.find((x) => String(x.id) === String(el.dataset.variantId));
        if (v) total += v.price;
      });
      return total;
    }

    _updateSum() {
      const sumEl = this.querySelector('[data-arcta-atc-sum]');
      if (!sumEl) return;
      const chosen = this.querySelectorAll('[data-arcta-atc-addon][aria-checked="true"]').length;
      const extras = this._extrasTotal();
      // Echte Warenkorbsumme (Zelt liegt beim Öffnen schon drin, inkl. Rabatten
      // und App-Beigaben) + die angehakten, noch nicht hinzugefügten Extras
      if (this._cartTotal != null) {
        let note = '';
        if (chosen) {
          note = ' <span class="arcta-atc__sum-note">inkl. ' + chosen + (chosen === 1 ? ' Extra' : ' Extras') + '</span>';
        }
        sumEl.innerHTML = 'Warenkorb gesamt: <strong>' + formatEuro(this._cartTotal + extras) + '</strong>' + note;
        sumEl.classList.remove('is-empty');
      } else {
        // Fallback (Warenkorb noch nicht gelesen): alte Anzeige
        if (!chosen) {
          sumEl.textContent = 'Keine Extras gewählt';
          sumEl.classList.add('is-empty');
        } else {
          const label = chosen === 1 ? 'Extra' : 'Extras';
          sumEl.textContent = chosen + ' ' + label + ' · + ' + formatEuro(extras);
          sumEl.classList.remove('is-empty');
        }
      }
    }

    // Gesamtsumme in Popup 2: echte Warenkorbsumme (inkl. Rabatten/App-Beigaben)
    _updateTotal2() {
      const el = this.querySelector('[data-arcta-atc-total2]');
      if (!el) return;
      el.textContent = formatEuro(this._cartTotal || 0);
    }

    // Warenkorb-Abgleich: /cart.js lesen und den Popup-Zustand daran ausrichten.
    // - Add-on-Kacheln, deren Produkt schon im Warenkorb liegt, bekommen das
    //   "Bereits im Warenkorb"-Badge und sind nicht mehr anhakbar.
    // - Gear-Stepper zeigen die ECHTE Warenkorb-Menge.
    // - Die Gesamtsumme ist die echte Warenkorbsumme (inkl. Rabatten).
    _refreshCartState() {
      fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } })
        .then((r) => r.json())
        .then((cart) => {
          const items = cart.items || [];
          this._cartTotal = cart.total_price || 0;

          // Popup 1: Add-ons, die (als Produkt) schon im Warenkorb liegen
          const handlesInCart = {};
          items.forEach((it) => { if (it.handle) handlesInCart[it.handle] = true; });
          this.querySelectorAll('[data-arcta-atc-addon]').forEach((el) => {
            const inCart = !!handlesInCart[el.dataset.arctaAtcAddon];
            el.classList.toggle('is-incart', inCart);
            const badge = el.querySelector('[data-arcta-atc-incart]');
            if (badge) badge.hidden = !inCart;
            if (inCart) el.setAttribute('aria-checked', 'false');
            const sel = el.querySelector('[data-arcta-atc-option2]');
            if (sel) sel.disabled = inCart;
          });

          // Popup 2: Stepper auf echte Warenkorb-Mengen setzen
          const qtyByVariant = {};
          items.forEach((it) => {
            qtyByVariant[String(it.variant_id)] = (qtyByVariant[String(it.variant_id)] || 0) + it.quantity;
          });
          this.querySelectorAll('[data-arcta-atc-qty]').forEach((wrap) => {
            this._setGearCount(wrap, qtyByVariant[String(wrap.dataset.variantId)] || 0);
          });

          this._updateSum();
          this._updateTotal2();
        })
        .catch(() => {});
    }
  }

  customElements.define('arcta-atc-popups', ArctaAtcPopups);

  // optionaler manueller Trigger
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-arcta-atc-trigger]');
    if (!trigger) return;
    const popups = document.querySelector('arcta-atc-popups');
    if (popups) popups.open(1);
  });
})();
